import { zodResolver } from "@hookform/resolvers/zod";
import { observer } from "mobx-react-lite";
import { forwardRef, useEffect } from "react";
import { useForm } from "react-hook-form";

import { CustomCardBody, Tags } from "@/components/atoms";
import { Form } from "@/components/ui/form";
import { cn } from "@/lib/utils";
import {
  ambianceList,
  dressCodeList,
  priceRangeList,
  tagsList,
} from "@/modules/mocksData";
import { rootStore } from "@/store/RootStore";
import type { FormProps, FormRefType } from "@/types/common";
import { useExposeFormValues } from "@/utils/hooks/useExposeFormValues";

import { type BrandVibesType, vibesSchema, type VibesType } from "./types";

// TODO: maybe delete this component after full integration with restaurant part
const allVibes: VibesType[] = [
  { title: "Tags", list: tagsList, name: "tags" },
  { title: "Ambiance", list: ambianceList, name: "ambiance" },
  { title: "Dress code", list: dressCodeList, name: "dressCode" },
  { title: "Price range", list: priceRangeList, name: "priceLevel" },
];

const BrandFormVibesInner = forwardRef<
  FormRefType<BrandVibesType>,
  FormProps<BrandVibesType>
>(({ defaultValues, onSubmit, onValidityChange, id, classes }, ref) => {
  const { isAdmin } = rootStore.authStore;

  const form = useForm<BrandVibesType>({
    resolver: zodResolver(vibesSchema),
    defaultValues,
    mode: "onChange",
    reValidateMode: "onBlur",
  });

  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = form;

  useExposeFormValues(ref, form);

  useEffect(() => {
    onValidityChange?.(isValid);
  }, [isValid, onValidityChange]);

  return (
    <Form {...form}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        id={id}
        data-testid="brand-form-vibes"
      >
        <CustomCardBody classes={cn("max-w-[610px]", classes)}>
          <div className="flex flex-col gap-4.5">
            {allVibes.map(list => (
              <Tags
                key={list.title}
                tagsList={list.list}
                title={list.title}
                form={control}
                name={list.name}
                disabled={list.name === "priceLevel" && !isAdmin}
              />
            ))}
          </div>
        </CustomCardBody>
      </form>
    </Form>
  );
});

BrandFormVibesInner.displayName = "BrandFormVibes";

const BrandFormVibes = observer(BrandFormVibesInner);
export default BrandFormVibes;
